import React, { useState, useEffect } from 'react';
import { Typography, List, IconButton, ListItem, ListItemText, ListItemAvatar, Avatar, Divider, SwipeableDrawer, LinearProgress, Checkbox, Button } from '@material-ui/core';
import Map from './Map';
import { fetchHomes } from './FetchHomes';
import '../../assets/styles/Step2/Step2.css';
import Box from '@mui/material/Box';
import { Global } from '@emotion/react';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import ComparablesTable from "../ComparablesTable/ComparablesTable";
import DetailedHomeView from "../DetailedHomeView";
import FilterForm from './FilterForm';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {consoleDevLog} from "../../apiConfig";

const Root = styled('div')(({ theme }) => ({
  height: '100%',
  backgroundColor: "#fff",
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: theme.palette.background.default,
  }),
}));

const StyledBox = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: grey[800],
  }),
  height: `calc(100vh - 80px)`,
  overflow: 'hidden',
}));

const Puller = styled('div')(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: grey[900],
  }),
}));

const Step2 = ({
  subjectProperty,
  currentStep,
  selectedHomes,
  setSelectedHomes,
  finalSelectedHomes,
  setFinalSelectedHomes,
  handlePreviousStep,
  setCurrentStep,
}) => {
  const [selectedHome, setSelectedHome] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [homes, setHomes] = useState([]);
  const [loading, setLoading] = useState(true);  // <-- Keep track of loading
  const [filters, setFilters] = useState({
    searchRadius: '',
    priceRange: '',
    listingStatus: '',
    dateRange: '',
    relevanceScore: '',
    bedrooms: [],
    bathrooms: [],
  });
  const [center, setCenter] = useState({
    lat: subjectProperty.Latitude,
    lng: subjectProperty.Longitude,
  });
  const [bounds, setBounds] = useState(null);
  const [radius, setRadius] = useState(null);
  const [tooManyHomes, setTooManyHomes] = useState(false);
  const [expandedHome, setExpandedHome] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);

  const [selectedHomesData, setSelectedHomesData] = useState(
    finalSelectedHomes.reduce((acc, home) => {
      acc[home.id] = home;
      return acc;
    }, {})
  );

  const handleListClick = (home) => {
    setSelectedHome(home);
    setCenter({ lat: home.Latitude, lng: home.Longitude });
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleCheckboxToggle = (home) => {
    setSelectedHomesData((prevSelected) => {
      const newSelected = { ...prevSelected };
      if (newSelected[home.id]) {
        delete newSelected[home.id];
      } else {
        newSelected[home.id] = home;
      }
      return newSelected;
    });
  };

  const onHomeDelete = (homeId) => {
    setSelectedHomesData((prevSelected) => {
      const newSelected = { ...prevSelected };
      delete newSelected[homeId];
      return newSelected;
    });
  };

  const formatPrice = (price) => {
    return price ? `$${new Intl.NumberFormat().format(price)}` : 'Unavailable';
  };

  useEffect(() => {
    applyFilters();
  }, [filters]);

  const handleExpandClick = (home) => {
    setExpandedHome(home);
    setModalOpen(true);
  };

  const handleExpandedClose = () => {
    setModalOpen(false);
  };

  const handleBoundsChange = (newBounds) => {
    setBounds(newBounds);
  };

  const applyFilters = () => {
    if (filters.searchRadius) {
      setRadius(filters.searchRadius * 1000);
    }
    if (bounds) {
      fetchHomes(bounds, filters, subjectProperty, setHomes, setTooManyHomes, setLoading);
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    consoleDevLog(`Map clicked at lat: ${lat}, lng: ${lng}`);
  };

  const onConfirmClick = (home) => {
    setSelectedHomesData((prevSelected) => {
      const newSelected = { ...prevSelected };
      if (newSelected[home.id]) {
        delete newSelected[home.id]; // unselect
      } else {
        newSelected[home.id] = home; // select
      }
      return newSelected;
    });
  };

  return (
    <Root>
      

      <Global
        styles={{
          '.MuiDrawer-root > .MuiPaper-root': {
            height: `calc(100% - 80px)`,
            overflow: 'visible',
          },
        }}
      />

      <SwipeableDrawer
        anchor="bottom"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={80}
        disableSwipeToOpen={false}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          style: {
            height: '90vh',
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            left: '210px'
          },
        }}
      >
        <StyledBox
          sx={{
            position: 'absolute',
            top: drawerOpen ? -20 : -80,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: 'visible',
            right: 0,
            left: 0,
            border: "2px solid black"
          }}
        >
          <Puller />
          {!drawerOpen && (
            <Typography variant="h6" sx={{ p: 2, color: 'text.secondary' }} style={{ marginTop: "30px", marginLeft: "30px", marginBottom: "30px"}}>
              {homes.length} results
            </Typography>
          )}
        </StyledBox>

        <StyledBox sx={{ display: 'flex', flexDirection: 'row', height: '100%' }}>
          {/* Sidebar Section */}
          <Box sx={{ flexBasis: '30%', display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingRight: 2 }}>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {/* Subject Property */}
              <Typography variant="h6" style={{ position: "relative", marginLeft: "20px" }}>
                Subject Property
              </Typography>
              <List>
                <ListItem
                  alignItems="flex-start"
                  button
                  onClick={() => handleListClick(subjectProperty)} // Still allow the entire row to be clickable
                >
                  <ListItemAvatar>
                    <Avatar 
                      variant="square" 
                      src={`https://realtime-home-images.s3.amazonaws.com/${subjectProperty.Ml_num}/1.jpeg`}
                      alt={subjectProperty.Addr} 
                      style={{ width: 70, height: 70 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={subjectProperty.Addr}
                    secondary={
                      <>
                        <Typography variant="body2" color="textSecondary">
                          List Price: {formatPrice(subjectProperty.Sp_dol)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" component="p">
                          Bedrooms: {subjectProperty.Br}, Bathrooms: {subjectProperty.Bath_tot}
                        </Typography>
                      </>
                    }
                    primaryTypographyProps={{ style: { color: '#000', fontWeight: 'bold' } }}
                    secondaryTypographyProps={{ style: { color: '#555' } }}
                    style={{ marginLeft: '15px' }}
                  />
                  {/* New IconButton beside the subject property listing */}
                  <IconButton 
                    onClick={(e) => {
                      // Prevent the ListItem's onClick from firing if we only want the icon to trigger
                      e.stopPropagation();
                      handleListClick(subjectProperty);
                    }} 
                    edge="end"
                    aria-label="Jump to Subject Property"
                  >
                    <KeyboardReturnIcon />
                  </IconButton>
                </ListItem>
                <Divider />
              </List>
              {/* Selected Comparables */}
              {Object.keys(selectedHomesData).length > 0 && (
                <Typography variant="h6" style={{ position: "relative", marginLeft: "20px" }}>
                  Selected Comparables
                </Typography>
              )}
              <List>
                {Object.values(selectedHomesData).map((home) => (
                  <React.Fragment key={`selected-${home.id}`}>
                    <ListItem alignItems="flex-start" button onClick={() => handleListClick(home)}>
                      <Checkbox
                        checked={!!selectedHomesData[home.id]}
                        onChange={() => handleCheckboxToggle(home)}
                        color="primary"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <ListItemAvatar>
                        <Avatar variant="square" src={home.image} alt={home.Addr} style={{ width: 70, height: 70 }} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={home.Addr}
                        secondary={
                          <>
                            <Typography variant="body2" color="textSecondary">
                              List Price: {formatPrice(home.Sp_dol)}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Distance: {home.distance ? `${home.distance} km` : 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" component="p">
                              Relevance Score: {home.score !== undefined ? home.score.toFixed(2) : 'N/A'}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={home.score || 0}
                              sx={{ marginTop: 1, height: 6, borderRadius: 3 }}
                            />
                          </>
                        }
                        primaryTypographyProps={{ style: { color: '#000', fontWeight: 'bold' } }}
                        secondaryTypographyProps={{ style: { color: '#555' } }}
                        style={{ marginLeft: '15px' }}
                      />
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              {/* Suggested Comparables */}
              <Typography variant="h6" style={{ position: "relative", marginLeft: "20px" }}>
                Suggested Comparables
              </Typography>
              <List>
                {homes
                  .filter(
                    (home) => !selectedHomesData[home.id] && home.Ml_num !== subjectProperty.Ml_num
                  )
                  .sort((a, b) => b.score - a.score)
                  .map((home, index) => (
                    <React.Fragment key={home.id}>
                      <ListItem alignItems="flex-start" button onClick={() => handleListClick(home)}>
                        <Checkbox
                          checked={!!selectedHomesData[home.id]}
                          onChange={() => handleCheckboxToggle(home)}
                          color="primary"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <ListItemAvatar>
                          <Avatar variant="square" src={home.image} alt={home.Addr} style={{ width: 70, height: 70 }} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={home.Addr}
                          secondary={
                            <>
                              <Typography variant="body2" color="textSecondary">
                                List Price: {formatPrice(home.Sp_dol)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                Distance: {home.distance ? `${home.distance} km` : 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" component="p">
                                Relevance Score: {home.score !== undefined ? home.score.toFixed(2) : 'N/A'}
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={home.score || 0}
                                sx={{ marginTop: 1, height: 6, borderRadius: 3 }}
                              />
                            </>
                          }
                          primaryTypographyProps={{ style: { color: '#000', fontWeight: 'bold' } }}
                          secondaryTypographyProps={{ style: { color: '#555' } }}
                          style={{ marginLeft: '15px' }}
                        />
                      </ListItem>
                      {index < homes.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
              </List>
            </Box>

            {/* Apply Button */}
            <Box sx={{ padding: 2, borderTop: `1px solid ${grey[300]}` }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => {
                  setFinalSelectedHomes(Object.values(selectedHomesData));
                  setDrawerOpen(false);
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>

          {/* Map and Filter Form Section */}
          <Box sx={{ flexBasis: '70%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Filter Form above the Map */}
            <Box sx={{ borderBottom: `1px solid ${grey[300]}` }}>
              <FilterForm
                filters={filters}
                setFilters={setFilters}
                onCancel={() => setDrawerOpen(false)}
                resultCount={homes.length}
                onSearchAddress={(lat, lng) => {
                  setCenter({ lat, lng });
                }}
              />
            </Box>

            

            {/* Map Section */}
            <Box sx={{ flex: 1 }}>
              {/* Show loading bar if fetching */}
              {loading && (
                <Box sx={{ width: '100%', position: 'absolute', zIndex: 999 }}>
                  <LinearProgress />
                </Box>
              )}
              <Map
                homes={homes}
                setHomes={setHomes}
                subjectProperty={subjectProperty}
                selectedHomes={Object.keys(selectedHomesData)}
                onConfirmClick={onConfirmClick}
                selectedHome={selectedHome}
                setSelectedHome={setSelectedHome}
                setSelectedHomes={setSelectedHomes}
                setFinalSelectedHomes={setFinalSelectedHomes}
                center={center}
                setCenter={setCenter}
                filters={filters}
                onBoundsChange={handleBoundsChange}
                fetchHomes={(bounds) => fetchHomes(bounds, filters, subjectProperty, setHomes, setTooManyHomes, setLoading)}
                radius={radius}
                tooManyHomes={tooManyHomes}
              />
            </Box>
          </Box>
        </StyledBox>
      </SwipeableDrawer>

      <div className="container">
        <ComparablesTable
          subjectProperty={subjectProperty}
          finalSelectedHomes={finalSelectedHomes}
          setFinalSelectedHomes={setFinalSelectedHomes}
          adjustments={{}}
          toggleDrawer={toggleDrawer}
          onHomeDelete={onHomeDelete}
          setCurrentStep={setCurrentStep}
          loading={false}
        />
        {expandedHome && (
          <DetailedHomeView
            home={expandedHome}
            open={isModalOpen}
            onClose={handleExpandedClose}
          />
        )}
      </div>
    </Root>
  );
};

export default Step2;
